import { useLanguage } from '../contexts/LanguageContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Globe, ChevronDown } from 'lucide-react';

export const LanguageSwitcher = ({ variant = 'default', className = '' }) => {
  const { language, setLanguage, languages } = useLanguage();

  // Pill variant for landing page header - styled as rounded pill button
  if (variant === 'pill') {
    return (
      <Select value={language} onValueChange={setLanguage}>
        <SelectTrigger 
          className={`w-auto gap-1.5 bg-white/80 hover:bg-white border border-primary/30 hover:border-primary/50 rounded-full px-3 py-2 h-[38px] shadow-sm transition-all duration-200 [&>svg:last-child]:hidden ${className}`}
          data-testid="language-switcher"
        >
          <Globe className="w-3.5 h-3.5 text-primary" />
          <span className="text-secondary font-medium text-sm uppercase">
            {language}
          </span>
          <ChevronDown className="w-3 h-3 text-muted-foreground" />
        </SelectTrigger>
        <SelectContent className="rounded-xl">
          {languages.map((lang) => (
            <SelectItem 
              key={lang.code} 
              value={lang.code}
              data-testid={`lang-option-${lang.code}`}
              className="rounded-lg"
            >
              {lang.nativeName}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  // Compact variant - minimal styling
  if (variant === 'compact') {
    return (
      <Select value={language} onValueChange={setLanguage}>
        <SelectTrigger 
          className={`w-auto gap-2 border-none bg-transparent hover:bg-orange-50 rounded-full px-3 ${className}`}
          data-testid="language-switcher"
        >
          <Globe className="w-4 h-4 text-muted-foreground" />
          <SelectValue>
            {languages.find(l => l.code === language)?.nativeName}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {languages.map((lang) => (
            <SelectItem 
              key={lang.code} 
              value={lang.code}
              data-testid={`lang-option-${lang.code}`}
            >
              {lang.nativeName}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  // Default variant - full width
  return (
    <Select value={language} onValueChange={setLanguage}>
      <SelectTrigger 
        className={`w-full rounded-xl ${className}`}
        data-testid="language-switcher"
      >
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4 text-muted-foreground" />
          <SelectValue>
            {languages.find(l => l.code === language)?.nativeName}
          </SelectValue>
        </div>
      </SelectTrigger>
      <SelectContent>
        {languages.map((lang) => (
          <SelectItem 
            key={lang.code} 
            value={lang.code}
            data-testid={`lang-option-${lang.code}`}
          >
            {lang.nativeName}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
